<template>
    <div class="relative">
        <button
            v-if="isDiscovery"
            ref="trigger"
            class="w-full rounded border border-slate-200 bg-white px-3 py-4 hover:border-slate-300"
            aria-label="Lists"
            aria-haspopup="true"
            :aria-expanded="dropdownOpen"
            @click.prevent="toggleDropdown"
        >
            <div class="flex flex-col text-left text-sm">
                <div class="flex">
                    <AppIcon
                        v-if="showTriggerPlatformIcon"
                        :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                        class="my-auto mr-3 h-4 w-4 shrink-0"
                        :class="platformId === 'twitter' ? 'text-x' : `text-${platformId}`"
                    />

                    <div class="flex min-w-0 flex-col">
                        <span
                            class="line-clamp-1 font-semibold leading-none"
                            :class="{ 'text-slate-400': !selectedSource && showNoSourceSelected }"
                            :title="triggerLabel"
                        >
                            {{ triggerLabel }}
                        </span>
                        <span
                            v-if="triggerMeta"
                            class="mt-1 line-clamp-1 text-xs leading-none text-slate-500"
                            :title="triggerMeta"
                        >
                            {{ triggerMeta }}
                        </span>
                    </div>

                    <span v-if="hasSelection" class="ml-auto h-6 w-6 shrink-0" />

                    <AppIcon
                        name="chevron-down"
                        class="my-auto mr-1 h-3.5 w-3.5 shrink-0 text-slate-400"
                        :class="hasSelection ? 'ml-2' : 'ml-auto'"
                    />
                </div>
            </div>
        </button>
        <button
            v-else
            ref="trigger"
            class="flex h-10 w-full items-center rounded border border-slate-200 bg-white px-3.5 py-1 text-left text-sm leading-none hover:border-slate-300"
            aria-label="Lists"
            aria-haspopup="true"
            :aria-expanded="dropdownOpen"
            @click.prevent="toggleDropdown"
        >
            <div class="flex min-w-0 flex-1 text-left text-sm leading-none">
                <div class="flex min-w-0 w-full items-center">
                    <template v-if="showNoSourceSelected && !selectedSource">
                        <span class="line-clamp-1 min-w-0 flex-1 text-slate-400">{{ noSourceSelectedMessage }}</span>
                    </template>
                    <template v-else>
                        <div class="min-w-0 flex-1">
                            <span class="line-clamp-1 block min-w-0 font-normal leading-none text-slate-900">{{
                                selectedSource?.value?.name ?? selectedSource?.name
                            }}</span>
                        </div>
                    </template>

                    <AppIcon
                        name="chevron-down"
                        class="my-auto ml-2 h-3.5 w-3.5 shrink-0 text-slate-400"
                    />
                </div>
            </div>
        </button>

        <button
            v-if="isDiscovery && hasSelection"
            type="button"
            class="absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Clear source selection"
            title="Clear source selection"
            @click.stop.prevent="clearSelection()"
        >
            <X class="h-3.5 w-3.5" />
        </button>

        <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-out duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="dropdownOpen"
                ref="dropdown"
                class="absolute left-0 z-50 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-xl scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-400 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-500 active:scrollbar-thumb-slate-600"
                :class="dropdownPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'"
                :style="{ maxHeight: `${dropdownMaxHeight}px` }"
            >
                <div v-if="useEnhancedSelection">
                    <div class="sticky left-0 top-0 z-20 space-y-2 border-b border-slate-200 bg-white p-2">
                        <div
                            v-if="showSourceTypeToggle"
                            class="grid grid-cols-2 rounded border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium"
                        >
                            <button
                                type="button"
                                class="rounded px-2 py-1.5"
                                :class="activeSourceType === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                                @click.stop="setActiveSourceType('list')"
                            >
                                Lists
                            </button>
                            <button
                                type="button"
                                class="rounded px-2 py-1.5"
                                :class="activeSourceType === 'campaign' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                                @click.stop="setActiveSourceType('campaign')"
                            >
                                Campaigns
                            </button>
                        </div>

                        <div class="flex gap-2">
                            <div class="relative min-w-0 flex-1">
                                <AppIcon
                                    name="search"
                                    class="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    v-model="searchText"
                                    type="search"
                                    class="form-input h-9 w-full rounded border-slate-200 pl-8 text-sm shadow-none focus:border-slate-300"
                                    :placeholder="searchPlaceholder"
                                    autocomplete="off"
                                    @click.stop
                                />
                            </div>

                            <div class="relative shrink-0" @click.stop>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    class="h-9 w-9 rounded border-slate-200 text-slate-500 hover:bg-slate-50"
                                    :aria-label="`Sort by ${activeSortLabel}`"
                                    :title="`Sort by ${activeSortLabel}`"
                                    @click.stop="toggleSortMenu"
                                >
                                    <ArrowDownUp class="h-4 w-4" />
                                </Button>

                                <div
                                    v-if="sortMenuOpen"
                                    class="absolute right-0 top-full z-30 mt-1 w-40 rounded-md border border-slate-200 bg-white p-1 shadow-md"
                                >
                                    <button
                                        v-for="option in sortOptions"
                                        :key="option.value"
                                        type="button"
                                        class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-slate-100"
                                        @click.stop="setSortMode(option.value)"
                                    >
                                        <component :is="option.icon" class="h-3.5 w-3.5 text-muted-foreground/70" />
                                        <span class="flex-1">{{ option.label }}</span>
                                        <Check v-if="sortMode === option.value" class="h-3.5 w-3.5 text-indigo-600" />
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>

                    <template v-if="activeSourceType === 'list'">
                        <label
                            v-for="list in visibleLists"
                            :key="list.id"
                            class="flex w-full cursor-pointer border-b border-slate-200 px-3 py-2.5 text-left hover:bg-slate-50"
                            @click.stop
                        >
                            <input
                                type="checkbox"
                                class="my-auto mr-2 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600"
                                :checked="isListSelected(list.id)"
                                @change.stop="toggleListSelection(list)"
                            />
                            <AppIcon
                                v-if="platformId"
                                :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                                class="my-auto mr-2 h-4 w-4 shrink-0"
                                :class="platformId === 'twitter' ? 'text-x' : `text-${platformId}`"
                            />

                            <div class="min-w-0 flex-1">
                                <span class="line-clamp-1 block text-sm font-medium leading-none" :title="list.name">{{ list.name }}</span>
                                <span class="mt-1 block text-xs leading-none text-slate-600">
                                    {{ formatSourceCount(list) }} creators
                                </span>
                            </div>
                        </label>
                        <div
                            v-if="!visibleLists.length"
                            class="border-b border-slate-200 px-3 py-3 text-center text-xs text-slate-500"
                        >
                            {{ searchText ? 'No lists match your search' : 'You have no lists' }}
                        </div>
                    </template>

                    <template v-else>
                        <label
                            v-for="campaign in visibleCampaigns"
                            :key="campaign.id"
                            class="flex w-full cursor-pointer border-b border-slate-200 px-3 py-2.5 text-left hover:bg-slate-50"
                            @click.stop
                        >
                            <input
                                type="checkbox"
                                class="my-auto mr-2 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600"
                                :checked="isCampaignSelected(campaign.id)"
                                @change.stop="toggleCampaignSelection(campaign)"
                            />
                            <AppIcon
                                v-if="platformId"
                                :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                                class="my-auto mr-2 h-4 w-4 shrink-0"
                                :class="platformId === 'twitter' ? 'text-x' : `text-${platformId}`"
                            />

                            <div class="min-w-0 flex-1">
                                <span class="line-clamp-1 block text-sm font-medium leading-none" :title="getSourceName('campaign', campaign)">{{ getSourceName('campaign', campaign) }}</span>
                                <span class="mt-1 block text-xs leading-none text-slate-600">
                                    {{ formatSourceCount(campaign) }} creators
                                    <template v-if="campaign.type">
                                        - {{ getFromDictionary(`campaign.type.${campaign.type}`, 'label') }}
                                    </template>
                                </span>
                            </div>
                        </label>
                        <div
                            v-if="!visibleCampaigns.length"
                            class="border-b border-slate-200 px-3 py-3 text-center text-xs text-slate-500"
                        >
                            {{ searchText ? 'No campaigns match your search' : 'You have no campaigns' }}
                        </div>
                    </template>
                </div>

                <div v-else>
                    <div
                        v-if="showNoSourceSelected && selectedSource"
                        class="text-md flex cursor-pointer border-b border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                        @click="onSelect()"
                    >
                        <span class="text-slate-400">{{ noSourceSelectedMessage }}</span>
                    </div>

                    <div
                        v-if="includePlatform"
                        class="sticky left-0 top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-semibold"
                    >
                        Platform
                    </div>
                    <template v-if="includePlatform">
                        <div
                            v-for="source in allSources.platform"
                            :key="source.id"
                            class="text-md flex cursor-pointer border-b border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                            @click="onSelect('platform', source)"
                        >
                            <AppIcon
                                :name="source.icon"
                                class="my-auto mr-2 h-4 w-4 shrink-0"
                                :class="source.iconClass"
                            />

                            <div class="ml-1.5 flex flex-col">
                                <span class="line-clamp-1 block text-sm font-medium leading-none" :title="source.name">{{ source.name }}</span>
                            </div>
                        </div>
                    </template>

                    <div
                        v-if="includeLists"
                        class="sticky left-0 top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-semibold"
                    >
                        Lists
                    </div>
                    <template v-if="includeLists">
                        <div
                            v-for="list in allSources.list"
                            :key="list.id"
                            class="flex cursor-pointer border-b border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                            @click="onSelect('list', list)"
                        >
                            <AppIcon
                                v-if="platformId"
                                :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                                class="my-auto mr-2 h-4 w-4 shrink-0"
                                :class="platformId === 'twitter' ? 'text-x' : `text-${platformId}`"
                            />

                            <div class="ml-1.5 flex flex-col">
                                <span class="line-clamp-1 block text-sm font-medium leading-none" :title="list.name">{{ list.name }}</span>
                                <span class="mt-1 block text-xs leading-none text-slate-600">
                                    {{ list.isUnknown ? 'N/A' : (platformId ? $filters.numeral(list.totals[`${platformId}`]).format('0,0') : $filters.numeral(list.totals.total).format('0,0')) }} creators
                                </span>
                            </div>
                        </div>
                    </template>
                    <div v-if="!allSources?.list?.length && includeLists" class="border-b border-slate-200 px-3 py-2 text-xs">
                        <p class="text-center">You have no lists</p>
                    </div>

                    <div
                        v-if="includeCampaigns"
                        class="sticky left-0 top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-semibold"
                    >
                        Campaigns
                    </div>
                    <template v-if="includeCampaigns">
                        <div
                            v-for="campaign in allSources.campaign"
                            :key="campaign.id"
                            class="flex cursor-pointer border-b border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                            @click="onSelect('campaign', campaign)"
                        >
                            <AppIcon
                                :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                                class="my-auto mr-2 h-4 w-4 shrink-0"
                                :class="platformId === 'twitter' ? 'text-x' : `text-${platformId}`"
                            />

                            <div class="ml-1.5 flex flex-col">
                                <span class="line-clamp-1 block text-sm font-medium leading-none" :title="campaign.title">{{ campaign.title }}</span>
                                <span class="mt-1 block text-xs leading-none text-slate-600">
                                    {{ $filters.formatWholeNumberWithUnit(campaign.totals[`${platformId}`]) }} creators -
                                    {{ getFromDictionary(`campaign.type.${campaign.type}`, 'label') }}
                                </span>
                            </div>
                        </div>
                    </template>
                    <div v-if="!allSources?.campaign?.length && includeCampaigns" class="border-b border-slate-200 px-3 py-2 text-xs">
                        <p class="text-center">You have no campaigns</p>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ref, onMounted, onUnmounted } from 'vue'
import _ from 'lodash'
import { ArrowDownUp, CalendarDays, Check, Clock3, UsersRound, X } from 'lucide-vue-next'
import Button from '@/components/ui/button/Button.vue'

export default {
    name: 'DropdownDiscoverSource',

    components: {
        ArrowDownUp,
        Button,
        CalendarDays,
        Check,
        Clock3,
        UsersRound,
        X,
    },

    props: {
        listsByPlatformId: {
            type: Object,
        },
        campaignsByPlatformId: {
            type: Object,
        },
        platformId: {
            type: String,
            default: 'twitch',
        },
        initialSelectedSource: {
            type: Object,
        },
        counts: {
            type: Object,
        },
        includePlatform: {
            type: Boolean,
            default : true
        },
        includeLists: {
            type: Boolean,
            default : true
        },
        includeCampaigns: {
            type: Boolean,
            default : true
        },
        showNoSourceSelected : {
            type: Boolean,
            default : false
        },
        noSourceSelectedMessage : {
            type: String,
            default: 'All creators',
            required: false
        },
        useEnhancedSelection: {
            type: Boolean,
            default: false,
        },
        allowMultipleLists: {
            type: Boolean,
            default: false,
        },
        allowMultipleCampaigns: {
            type: Boolean,
            default: false,
        },
        isDiscovery: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['change'],

    data() {
        return {
            selectedSource: null,
            dropdownPlacement: 'bottom',
            dropdownMaxHeight: 384,
            searchText: '',
            sortMode: 'recency',
            sortMenuOpen: false,
            activeSourceType: 'list',

            platforms: {
                twitch: {
                    icon: 'twitch',
                    iconClass: 'text-twitch',
                    name: 'All Active Creators',
                    id: 'twitch',
                    description: 'You are searching on our database of creators on Twitch.',
                },
                youtube: {
                    icon: 'youtube',
                    iconClass: 'text-youtube',
                    name: 'All Active Creators',
                    id: 'youtube',
                    description: 'You are searching on our database of creators on YouTube.',
                },
                tiktok: {
                    icon: 'tiktok',
                    iconClass: 'text-tiktok',
                    name: 'All Active Creators',
                    id: 'tiktok',
                    description: 'You are searching on our database of creators on TikTok.',
                },
                twitter: {
                    icon: 'x-twitter',
                    iconClass: 'text-x',
                    name: 'All Active Creators',
                    id: 'twitter',
                    description: 'You are searching on our database of creators on X.',
                },
                instagram: {
                    icon: 'instagram',
                    iconClass: 'text-instagram',
                    name: 'All Active Creators',
                    id: 'instagram',
                    description: 'You are searching on our database of creators on Instagram.',
                },
            },
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary']),

        allSources() {
            return {
                platform: _.filter(this.platforms, (platform) => platform.id === this.platformId),
                list: this.listsByPlatformId?.[this.platformId] ?? this.allLists,
                campaign: _.orderBy(this.campaignsByPlatformId?.[this.platformId] || [], ['started_at'], ['desc']),
            }
        },

        allLists() {
            const combined = []
            const duplicateCheck = {}

            _.forOwn(this.platforms, platform => {
                const id = platform.id
                if (this.listsByPlatformId?.[id]) {
                    _.forEach(this.listsByPlatformId[id], list => {
                        if (!duplicateCheck[list.id]) {
                            combined.push(list)
                            duplicateCheck[list.id] = true
                        }
                    })
                }
            })

            return combined
        },

        selectedListIds() {
            if (this.selectedSource?.type === 'mixed') return this.normalizeIds(this.selectedSource.input?.list_id)
            if (this.selectedSource?.type !== 'list') return []
            return this.normalizeIds(this.selectedSource.input)
        },

        selectedCampaignIds() {
            if (this.selectedSource?.type === 'mixed') return this.normalizeIds(this.selectedSource.input?.campaign_id)
            if (this.selectedSource?.type !== 'campaign') return []
            return this.normalizeIds(this.selectedSource.input)
        },

        hasSelection() {
            return !!this.selectedSource?.type && this.selectedSource.type !== 'platform'
        },

        showTriggerPlatformIcon() {
            return !!this.platformId && !(this.showNoSourceSelected && !this.selectedSource)
        },

        showSourceTypeToggle() {
            return this.includeLists && this.includeCampaigns
        },

        searchPlaceholder() {
            return this.activeSourceType === 'campaign' ? 'Search campaigns' : 'Search lists'
        },

        sortOptions() {
            return [
                { value: 'recency', label: 'Last updated', icon: 'Clock3' },
                { value: 'date', label: 'Last created', icon: 'CalendarDays' },
                { value: 'size', label: 'Size', icon: 'UsersRound' },
            ]
        },

        activeSortLabel() {
            return _.find(this.sortOptions, { value: this.sortMode })?.label || 'Last updated'
        },

        visibleLists() {
            return this.sortSources(this.filterSources(this.allSources.list, 'list'), 'list')
        },

        visibleCampaigns() {
            return this.sortSources(this.filterSources(this.allSources.campaign, 'campaign'), 'campaign')
        },

        triggerLabel() {
            if (!this.selectedSource) {
                return this.showNoSourceSelected ? this.noSourceSelectedMessage : 'All active creators'
            }

            if (this.selectedSource.type === 'mixed') {
                return this.getCombinedSelectionName(this.selectedListIds, this.selectedCampaignIds)
            }

            if (this.selectedSource.type === 'list') {
                const lists = _.castArray(this.selectedSource.value || []).filter(Boolean)
                const selectedCount = this.selectedListIds.length
                if (selectedCount === 1 && lists.length === 1) return lists[0].name || this.selectedSource.name
                if (selectedCount > 1) return `${selectedCount} lists selected`
                return this.selectedSource.name || `${selectedCount} lists selected`
            }

            if (this.selectedSource.type === 'campaign') {
                const campaigns = _.castArray(this.selectedSource.value || []).filter(Boolean)
                const selectedCount = this.selectedCampaignIds.length
                if (selectedCount === 1 && campaigns.length === 1) return this.getSourceName('campaign', campaigns[0]) || this.selectedSource.name
                if (selectedCount > 1) return `${selectedCount} campaigns selected`
                return this.selectedSource.name || this.getSourceName('campaign', this.selectedSource.value) || 'Campaign selected'
            }

            return this.selectedSource?.value?.name ?? this.selectedSource?.name
        },

        triggerMeta() {
            if (!this.selectedSource) return null

            if (this.selectedSource.type === 'mixed') {
                const sourceNames = [
                    ..._.map(this.getSelectedLists(), 'name'),
                    ..._.map(this.getSelectedCampaigns(), (campaign) => this.getSourceName('campaign', campaign)),
                ].filter(Boolean)

                return sourceNames.slice(0, 3).join(', ')
            }

            if (this.selectedSource.type === 'list') {
                const lists = _.castArray(this.selectedSource.value || []).filter(Boolean)
                if (lists.length <= 1) return null
                return _.map(lists, 'name').slice(0, 3).join(', ')
            }

            if (this.selectedSource.type === 'campaign' && this.selectedSource.value) {
                const campaigns = _.castArray(this.selectedSource.value || []).filter(Boolean)
                if (campaigns.length > 1) return _.map(campaigns, (campaign) => this.getSourceName('campaign', campaign)).slice(0, 3).join(', ')
                if (campaigns.length === 1) return `${this.formatSourceCount(campaigns[0])} creators`
                return null
            }

            return null
        },
    },

    watch: {
        platformId(newValue, oldValue) {
            if (newValue !== oldValue) {
                if (this.useEnhancedSelection) {
                    this.activeSourceType = this.includeLists ? 'list' : 'campaign'
                    this.searchText = ''
                    this.clearSelection(true)
                    return
                }

                if (this.showNoSourceSelected) {
                    this.onSelect()
                }
                else {
                    this.onSelect('platform', this.platforms[newValue], true)
                }
            }
        },

        listsByPlatformId: {
            handler() {
                this.setInitialSelectedSource()
            },
            deep: true,
            immediate: false,
        },

        campaignsByPlatformId: {
            handler() {
                this.setInitialSelectedSource()
            },
            deep: true,
            immediate: false,
        },

        initialSelectedSource: {
            handler() {
                this.setInitialSelectedSource()
            },
            deep: true,
            immediate: false,
        },

        counts(newCounts) {
            _.forEach(newCounts?.discovery, (count, platformId) => {
                if (this.platforms[platformId]) {
                    this.platforms[platformId].platform_creator_count = count
                }
            })
        },
    },

    created() {
        if (this.useEnhancedSelection) {
            this.activeSourceType = this.includeLists ? 'list' : 'campaign'
            this.setInitialSelectedSource()
            return
        }

        if (!this.showNoSourceSelected) {
            this.onSelect('platform', this.platforms[this.platformId], true)
        } else {
            this.setInitialSelectedSource()
        }
    },

    methods: {
        toggleDropdown() {
            if (this.dropdownOpen) {
                this.dropdownOpen = false
                this.sortMenuOpen = false
                return
            }

            this.sortMenuOpen = false
            this.updateDropdownPlacement()
            this.dropdownOpen = true
        },

        updateDropdownPlacement() {
            const triggerEl = this.trigger?.$el || this.trigger
            if (!triggerEl) return

            const viewportPadding = 8
            const preferredDropdownHeight = 384
            const minimumDropdownHeight = 120
            const rect = triggerEl.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
            const spaceAbove = rect.top - viewportPadding
            const shouldOpenAbove = spaceBelow < preferredDropdownHeight && spaceAbove > spaceBelow
            const availableHeight = Math.max(
                minimumDropdownHeight,
                Math.min(preferredDropdownHeight, shouldOpenAbove ? spaceAbove : spaceBelow),
            )

            this.dropdownPlacement = shouldOpenAbove ? 'top' : 'bottom'
            this.dropdownMaxHeight = availableHeight
        },

        onSelect(type, source, isInitial = false) {
            if (!type) {
                this.clearSelection(isInitial)
                this.dropdownOpen = false
                this.sortMenuOpen = false
                return
            }

            this.selectedSource = {
                type,
                input: source.id,
                value: source,
                name: this.getSourceName(type, source),
            }

            if (!isInitial) {
                this.$emit('change', this.selectedSource)
                this.dropdownOpen = false
                this.sortMenuOpen = false
            }
        },

        setActiveSourceType(type) {
            this.activeSourceType = type
            this.sortMenuOpen = false
        },

        toggleSortMenu() {
            this.sortMenuOpen = !this.sortMenuOpen
        },

        setSortMode(mode) {
            this.sortMode = mode
            this.sortMenuOpen = false
        },

        toggleListSelection(list) {
            this.sortMenuOpen = false

            const currentLists = this.getSelectedLists()
            const isSelected = _.includes(this.selectedListIds, list.id)
            const nextLists = isSelected
                ? _.filter(currentLists, (item) => item.id !== list.id)
                : [...currentLists, list]

            if (!this.allowMultipleLists && nextLists.length > 1) {
                this.setListSelection([list])
                return
            }

            this.setListSelection(nextLists)
        },

        setListSelection(lists, isInitial = false, selectedIds = null) {
            const ids = selectedIds || _.map(lists, 'id')
            this.setCombinedSelection(lists, this.getSelectedCampaigns(), isInitial, ids, this.selectedCampaignIds)
        },

        toggleCampaignSelection(campaign) {
            this.sortMenuOpen = false

            const currentCampaigns = this.getSelectedCampaigns()
            const isSelected = _.includes(this.selectedCampaignIds, campaign.id)
            const nextCampaigns = isSelected
                ? _.filter(currentCampaigns, (item) => item.id !== campaign.id)
                : [...currentCampaigns, campaign]

            if (!this.allowMultipleCampaigns && nextCampaigns.length > 1) {
                this.setCampaignSelection([campaign])
                return
            }

            this.setCampaignSelection(nextCampaigns)
        },

        setCampaignSelection(campaigns, isInitial = false, selectedIds = null) {
            const ids = selectedIds || _.map(campaigns, 'id')
            this.setCombinedSelection(this.getSelectedLists(), campaigns, isInitial, this.selectedListIds, ids)
        },

        setCombinedSelection(lists, campaigns, isInitial = false, selectedListIds = null, selectedCampaignIds = null) {
            const listIds = selectedListIds || _.map(lists, 'id')
            const campaignIds = selectedCampaignIds || _.map(campaigns, 'id')

            if (!listIds.length && !campaignIds.length) {
                this.clearSelection(isInitial)
                return
            }

            if (listIds.length && campaignIds.length) {
                this.selectedSource = {
                    type: 'mixed',
                    input: {
                        list_id: listIds,
                        campaign_id: campaignIds,
                    },
                    value: {
                        lists,
                        campaigns,
                    },
                    name: this.getCombinedSelectionName(listIds, campaignIds),
                }
            } else if (listIds.length) {
                this.selectedSource = {
                    type: 'list',
                    input: listIds,
                    value: lists,
                    name: this.getListSelectionName(lists, listIds),
                }
            } else {
                this.selectedSource = {
                    type: 'campaign',
                    input: campaignIds,
                    value: campaigns,
                    name: this.getCampaignSelectionName(campaigns, campaignIds),
                }
            }

            if (!isInitial) {
                this.$emit('change', this.selectedSource)
            }
        },

        clearSelection(isInitial = false) {
            this.selectedSource = null
            this.sortMenuOpen = false

            if (!isInitial) {
                this.$emit('change')
            }
        },

        setInitialSelectedSource() {
            if (!this.initialSelectedSource || this.initialSelectedSource.type === 'platform') {
                if (this.showNoSourceSelected || this.useEnhancedSelection) {
                    this.selectedSource = null
                }
                return
            }

            if (this.initialSelectedSource.type === 'list' && this.useEnhancedSelection) {
                const selectedIds = this.normalizeIds(this.initialSelectedSource.input)
                const matchingLists = _.filter(this.allSources.list || [], (item) => _.includes(selectedIds, item.id))

                if (selectedIds.length && (matchingLists.length || this.initialSelectedSource.name)) {
                    this.activeSourceType = 'list'
                    this.setListSelection(matchingLists, true, selectedIds)
                } else if (this.showNoSourceSelected) {
                    this.selectedSource = null
                }
                return
            }

            if (this.initialSelectedSource.type === 'campaign' && this.useEnhancedSelection) {
                const selectedIds = this.normalizeIds(this.initialSelectedSource.input)
                const matchingCampaigns = _.filter(this.allSources.campaign || [], (item) => _.includes(selectedIds, item.id))

                if (selectedIds.length && (matchingCampaigns.length || this.initialSelectedSource.name)) {
                    this.activeSourceType = 'campaign'
                    this.setCampaignSelection(matchingCampaigns, true, selectedIds)
                } else if (this.showNoSourceSelected) {
                    this.selectedSource = null
                }
                return
            }

            if (this.initialSelectedSource.type === 'mixed' && this.useEnhancedSelection) {
                const selectedListIds = this.normalizeIds(this.initialSelectedSource.input?.list_id)
                const selectedCampaignIds = this.normalizeIds(this.initialSelectedSource.input?.campaign_id)
                const matchingLists = _.filter(this.allSources.list || [], (item) => _.includes(selectedListIds, item.id))
                const matchingCampaigns = _.filter(this.allSources.campaign || [], (item) => _.includes(selectedCampaignIds, item.id))

                if (selectedListIds.length || selectedCampaignIds.length) {
                    this.activeSourceType = this.getActiveSourceTypeForSelection(selectedListIds, selectedCampaignIds)
                    this.setCombinedSelection(matchingLists, matchingCampaigns, true, selectedListIds, selectedCampaignIds)
                } else if (this.showNoSourceSelected) {
                    this.selectedSource = null
                }
                return
            }

            const input = _.first(this.normalizeIds(this.initialSelectedSource.input))
            const matchingSource = _.find(
                this.allSources[this.initialSelectedSource.type] || [],
                (item) => item.id === input,
            )

            if (matchingSource) {
                this.activeSourceType = this.initialSelectedSource.type
                this.selectedSource = {
                    type: this.initialSelectedSource.type,
                    input,
                    value: matchingSource,
                    name: this.initialSelectedSource.name || this.getSourceName(this.initialSelectedSource.type, matchingSource),
                }
            } else if (this.showNoSourceSelected) {
                this.selectedSource = null
            }
        },

        clear() {
            this.selectedSource = null
        },

        normalizeIds(value) {
            return _.castArray(value).filter(Boolean)
        },

        getSelectedLists() {
            if (this.selectedSource?.type === 'mixed') return _.castArray(this.selectedSource.value?.lists || []).filter(Boolean)
            if (this.selectedSource?.type === 'list') return _.castArray(this.selectedSource.value || []).filter(Boolean)
            return []
        },

        getSelectedCampaigns() {
            if (this.selectedSource?.type === 'mixed') return _.castArray(this.selectedSource.value?.campaigns || []).filter(Boolean)
            if (this.selectedSource?.type === 'campaign') return _.castArray(this.selectedSource.value || []).filter(Boolean)
            return []
        },

        isListSelected(listId) {
            return _.includes(this.selectedListIds, listId)
        },

        isCampaignSelected(campaignId) {
            return _.includes(this.selectedCampaignIds, campaignId)
        },

        getActiveSourceTypeForSelection(listIds, campaignIds) {
            if (listIds.length && campaignIds.length) return this.activeSourceType
            return campaignIds.length ? 'campaign' : 'list'
        },

        getSourceName(type, source) {
            return type === 'campaign' ? source?.title || source?.name : source?.name
        },

        getListSelectionName(lists, ids) {
            if (ids.length === 1 && lists.length === 1) return lists[0].name
            if (ids.length === 1) return '1 list selected'
            return `${ids.length} lists selected`
        },

        getCampaignSelectionName(campaigns, ids) {
            if (ids.length === 1 && campaigns.length === 1) return this.getSourceName('campaign', campaigns[0])
            if (ids.length === 1) return '1 campaign selected'
            return `${ids.length} campaigns selected`
        },

        getCombinedSelectionName(listIds, campaignIds) {
            const listText = listIds.length === 1 ? '1 list' : `${listIds.length} lists`
            const campaignText = campaignIds.length === 1 ? '1 campaign' : `${campaignIds.length} campaigns`
            return `${listText}, ${campaignText} selected`
        },

        getSourceCount(source) {
            const count = this.platformId
                ? _.get(source, `totals.${this.platformId}`, 0)
                : _.get(source, 'totals.total', 0)
            return Number(count) || 0
        },

        formatSourceCount(source) {
            if (source?.isUnknown) return 'N/A'
            return this.$filters.numeral(this.getSourceCount(source)).format('0,0')
        },

        filterSources(sources, type) {
            const search = this.searchText.trim().toLowerCase()
            if (!search) return sources || []

            return _.filter(sources || [], (source) => {
                const name = this.getSourceName(type, source) || ''
                const campaignType = type === 'campaign' && source?.type
                    ? this.getFromDictionary(`campaign.type.${source.type}`, 'label') || ''
                    : ''
                return `${name} ${campaignType}`.toLowerCase().includes(search)
            })
        },

        sortSources(sources, type) {
            return _.orderBy(
                sources || [],
                [
                    (source) => this.getSortValue(source, type),
                    (source) => (this.getSourceName(type, source) || '').toLowerCase(),
                ],
                ['desc', 'asc'],
            )
        },

        getSortValue(source, type) {
            if (this.sortMode === 'size') {
                return this.getSourceCount(source)
            }

            if (this.sortMode === 'date') {
                return this.getTimestamp(type === 'campaign' ? source?.started_at || source?.created_at : source?.created_at)
            }

            return this.getTimestamp(source?.updated_at || source?.started_at || source?.created_at)
        },

        getTimestamp(value) {
            if (!value) return 0
            const timestamp = new Date(value).getTime()
            return Number.isNaN(timestamp) ? 0 : timestamp
        },
    },

    setup() {
        const dropdownOpen = ref(false)
        const dropdown = ref(null)
        const trigger = ref(null)

        const clickHandler = ({ target }) => {
            if (!dropdownOpen.value || dropdown.value?.contains(target) || trigger.value?.contains(target)) return
            dropdownOpen.value = false
        }

        const keyHandler = ({ keyCode }) => {
            if (!dropdownOpen.value || keyCode !== 27) return
            dropdownOpen.value = false
        }

        onMounted(() => {
            document.addEventListener('click', clickHandler)
            document.addEventListener('keydown', keyHandler)
        })

        onUnmounted(() => {
            document.removeEventListener('click', clickHandler)
            document.removeEventListener('keydown', keyHandler)
        })

        return {
            dropdownOpen,
            dropdown,
            trigger,
        }
    }
}
</script>
